import { Injectable, ComponentFactoryResolver, Injector, Inject, Type, Optional } from "@angular/core";
import { FormGroup, FormBuilder, AbstractControl, FormArray } from "@angular/forms";
import { FormFieldSetting, RootSetting } from "../form-settings/form-field-setting";
import { FormModel } from "./form-model";
import { FormContext } from "./form-context";
import { FormRootDirective } from "./form-root.directive";
import { FieldFactory, ParentFieldFactory } from "../form-field/field-factory";
import { GroupFieldFactory } from "../form-field/group/group-field-factory";
import { ArrayFieldFactory } from "../form-field/array/array-field-factory";
import { SingleFieldFactory } from "../form-field/single/single-field-factory";
import { InlineForm } from "./inline/inline-form";
import {
  createFormFieldSettings,
  FormFieldSettings,
  ROOT_ID,
  ensureDefaultFormComponents
} from "../form-settings/form-field-settings";
import { DEFAULT_FORM_COMPONENTS, DynamicFormType, FormComponentsByType } from "../form-field/form-field-type";
import { FormFieldGroupComponent } from "../form-field/group/form-field-group-component";
import { InlineFormFactory } from "./inline/inline-form-factory";
import { HiddenFieldFactory } from "../form-field/hidden/hidden-field-factory";

/**
 * A service for creating and rendering dynamic forms. For usage informations refer to
 * https://github.com/tom-schoener/angular-dynamic-forms-lite
 */
@Injectable({
  providedIn: "root"
})
export class DynamicFormsLiteService {
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    @Optional()
    @Inject(DEFAULT_FORM_COMPONENTS)
    private defaultFormComponents?: FormComponentsByType
  ) {}

  /**
   * Creates a form context from a seperated model and form settings. This is useful for advanced
   * forms, where the model is fetched from a server.
   *
   * @param formModel form model
   * @param formFieldSettings settings for the form model
   * @param rootSetting settings for the root of the form
   */
  public create<M = any>(
    formModel: FormModel,
    formFieldSettings: FormFieldSettings,
    rootSetting: RootSetting = this.createRootSetting()
  ): FormContext<M, FormGroup> {
    const settings = createFormFieldSettings(rootSetting, formFieldSettings, this.defaultFormComponents);

    return this.createParentFieldFactory(settings).create<M>({
      initialValue: formModel,
      setting: settings.get(ROOT_ID),
      settings
    }) as FormContext<M, FormGroup>;
  }

  /**
   * Creates a dynamic form context which can be used to render a form. This is useful for simple forms, where
   * the model is basically empty. For an advanced usecase with a seperate model and form settings see 'create'.
   *
   * @param inlineForm form, where the model and settings are combined
   * @param rootSetting settings for the root of the form
   */
  public createInline(
    inlineForm: InlineForm,
    rootSetting: RootSetting = this.createRootSetting()
  ): FormContext<any, FormGroup> {
    const inlineFormFactory: InlineFormFactory = new InlineFormFactory(this.defaultFormComponents);
    const settings: FormFieldSettings = inlineFormFactory.resolveSettings(inlineForm);
    const formModel: FormModel = inlineFormFactory.resolveModel(inlineForm);

    return this.create(formModel, settings, rootSetting);
  }

  /**
   * Renders the form at the specified form root recursively.
   *
   * @param formRoot root element (directive) to insert the form into
   * @param formContext form context
   */
  public render(formRoot: FormRootDirective, formContext: FormContext<any, FormGroup | FormArray>): void {
    this.createParentFieldFactory(formContext.settings).render(formRoot, formContext);
  }

  private createRootSetting(): RootSetting {
    return {
      component: ensureDefaultFormComponents(this.defaultFormComponents, "root", DynamicFormType.GROUP) as Type<
        FormFieldGroupComponent
      >,
      metadata: null
    };
  }

  private createParentFieldFactory(settings: FormFieldSettings): FieldFactory<AbstractControl, FormFieldSetting<any>> {
    return new ParentFieldFactory(
      {
        [DynamicFormType.GROUP]: GroupFieldFactory,
        [DynamicFormType.ARRAY]: ArrayFieldFactory,
        [DynamicFormType.SINGLE]: SingleFieldFactory,
        [DynamicFormType.HIDDEN]: HiddenFieldFactory
      },
      this.componentFactoryResolver,
      this.injector,
      new FormBuilder(),
      settings
    );
  }
}
